import React, { useReducer, useCallback, createContext, useContext } from 'react';

// Year and Month is a minimum required arguments for creating a date
// == null is intentional to check also for undefined
const newDate = (Y, M, ...rest) => !Y || M == null ? new Date() : new Date(Y, M, ...rest);
const getDateParts = (d) => ({
    D: d.getDate(),
    M: d.getMonth(),
    Y: d.getFullYear(),
});
// Days in order sun = 0 ... sat = 6
const getDay = (d) => d.getDay();
/*
 * We need this function to eliminate time from the comparison.
 * All date that comes to DP should go through this function.
 */
const getCleanDate = (d) => newDate(getDateParts(d).Y, getDateParts(d).M, getDateParts(d).D);
const daysInMonth = (d) => newDate(getDateParts(d).Y, getDateParts(d).M + 1, 0).getDate();
const getFirstDayOfTheMonth = (d) => newDate(getDateParts(d).Y, getDateParts(d).M, 1);
const addToDate = (d, value, part) => {
    const { Y, M, D } = getDateParts(d);
    // Cover case when offsetDate is 31 and next month doesn't have 31 days
    // More details here https://github.com/rehookify/datepicker/issues/10
    const nextDate = part === 'date'
        ? D + value
        : part === 'month' && D > daysInMonth(newDate(Y, M + value, 1))
            ? daysInMonth(newDate(Y, M + value, 1))
            : D;
    return newDate(Y + (part === 'year' ? value : 0), M + (part === 'month' ? value : 0), nextDate);
};
const subtractFromDate = (d, value, part) => addToDate(d, 0 - value, part);
const sortDatesAsc = (a, b) => +a - +b;
const toLocaleDateString = (d, locale, options) => d.toLocaleDateString(locale, options);
const formatMonthName = (d, { locale, monthName }) => toLocaleDateString(d, locale, { month: monthName });
const formatDate = (d, { locale, options }) => toLocaleDateString(d, locale, options);
const getTimeDate = (Y, M, D, t) => t && t.h != null && t.m != null ? newDate(Y, M, D, t.h, t.m) : null;
const formatTime = (d, { locale, hour, minute, second, hour12 }) => d.toLocaleTimeString(locale, {
    hour,
    minute,
    second,
    hour12,
});
const addAndSortAsc = (dates, d) => dates.concat(d).sort(sortDatesAsc);

/*
 * It is used for static 🗓 to cover all possible month start and end date combination
 * One month could take 6 rows by 7 days
 * 27 28 29 30 31 01 02
 * 03 04 05 06 07 08 09
 * 10 11 12 13 14 15 16
 * 17 18 19 20 21 22 23
 * 24 25 26 27 28 29 30
 * 31 01 02 03 04 05 06
 */
const NUMBER_OF_STATIC_CALENDAR_DAYS = 42;
//Weekdays array template, each index mimics its position
const WEEK_DAYS = [0, 1, 2, 3, 4, 5, 6];
const NUMBER_OF_MONTHS = 12;
const MINUTES_IN_THE_DAY = 60 * 24; // 1440 :)
// Number of yearn by default to mimic number of month
// It will be easy to reuse same layout for years picker
const DECADE_NUMBER_OF_YEARS = 12;
const DEFAULT_YEARS_STEP = 10;
const DEFAULT_CALENDAR_CONFIG = {
    mode: 'static',
    offsets: [0],
    startDay: 0,
};
const DEFAULT_YEARS_CONFIG = {
    mode: 'decade',
    numberOfYears: DECADE_NUMBER_OF_YEARS,
    step: DEFAULT_YEARS_STEP,
};
const DEFAULT_DATES_CONFIG = {
    mode: 'single',
    toggle: false,
    selectSameDate: false,
};
const DEFAULT_TIME_CONFIG = {
    interval: 30,
};
const DEFAULT_LOCALE_CONFIG = {
    locale: 'en-GB',
    day: '2-digit',
    year: 'numeric',
    weekday: 'short',
    monthName: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: undefined,
    second: undefined,
};

const getStartOffset = (d, startDay) => (getDay(d) + 7 - startDay) % 7;
const getCalendarMonthParams = (month, year, { mode, startDay }) => {
    const firstMonthDay = newDate(year, month, 1);
    const lastDay = daysInMonth(firstMonthDay);
    const startOffset = getStartOffset(firstMonthDay, startDay);
    const length = mode === 'static'
        ? NUMBER_OF_STATIC_CALENDAR_DAYS
        : startOffset +
            lastDay +
            6 -
            getStartOffset(newDate(year, month, lastDay), startDay);
    return {
        start: startOffset,
        length,
    };
};

const sortMinMax = (min, max, sortFunction) => {
    let [mN, mX] = [min, max];
    if (min && max) {
        [mN, mX] = [min, max].sort(sortFunction);
    }
    return [mN, mX];
};
const createConfig = ({ selectedDates = [], focusDate = null, onDatesChange, calendar = {}, dates = {}, locale, time = {}, years, } = {}) => {
    const { minDate, maxDate, ...restDates } = dates;
    const { offsets = [], ...restCalendarParams } = calendar;
    const { minTime, maxTime, ...restTime } = time;
    const [minD, maxD] = sortMinMax(minDate, maxDate, sortDatesAsc);
    const [minT, maxT] = sortMinMax(minTime, maxTime, (a, b) => a.h - b.h);
    const focus = focusDate && selectedDates.includes(focusDate) ? focusDate : null;
    return {
        selectedDates,
        focusDate: focus,
        onDatesChange,
        calendar: {
            ...DEFAULT_CALENDAR_CONFIG,
            ...restCalendarParams,
            offsets: DEFAULT_CALENDAR_CONFIG.offsets.concat(offsets),
        },
        years: { ...DEFAULT_YEARS_CONFIG, ...years },
        dates: {
            ...DEFAULT_DATES_CONFIG,
            ...restDates,
            minDate: minD ? getCleanDate(minD) : null,
            maxDate: maxD ? getCleanDate(maxD) : null,
        },
        locale: {
            ...DEFAULT_LOCALE_CONFIG,
            ...locale,
        },
        time: {
            ...DEFAULT_TIME_CONFIG,
            minTime: minT || null,
            maxTime: maxT || null,
            ...restTime,
        },
    };
};
const isRange = (mode) => mode === 'range';

// Converting Date to Number is equal of calling Date.getTime
const isSame = (d1, d2) => +d1 === +d2;
const isBefore = (d1, d2) => d1 < d2;
const isAfter = (d1, d2) => d1 > d2;
const isBetween = (start, d, end) => (isAfter(d, start) && isBefore(d, end)) ||
    (isBefore(d, start) && isAfter(d, end));
const maxDateAndAfter = (maxDate, date) => !!maxDate && isAfter(date, maxDate);
const minDateAndBefore = (minDate, date) => !!minDate && isBefore(date, minDate);
const minDateAndBeforeFirstDay = (minDate, date) => !!minDate && isBefore(date, getFirstDayOfTheMonth(minDate));
const includeDate = (dates, d) => dates.some((date) => isSame(getCleanDate(date), d));

const RANGE_START = 'range-start';
const RANGE_END = 'range-end';
const WILL_BE_IN_RANGE_START = 'will-be-range-start';
const WILL_BE_IN_RANGE_END = 'will-be-range-end';
const getDateRangeState = (date, rangeEnd, selectedDates, mode) => {
    if (!isRange(mode) || selectedDates.length === 0)
        return '';
    const [start, end] = selectedDates;
    // We have completed range
    if (start && end) {
        if (isSame(date, getCleanDate(start))) {
            return isSame(getCleanDate(start), getCleanDate(end))
                ? `${RANGE_START} ${RANGE_END}`
                : RANGE_START;
        }
        if (isSame(date, getCleanDate(end)))
            return RANGE_END;
        return isBetween(getCleanDate(start), date, getCleanDate(end))
            ? 'in-range'
            : '';
    }
    // We have 1 date and rangeEnd date
    if (!end && rangeEnd) {
        if (isBetween(getCleanDate(start), date, rangeEnd))
            return 'will-be-in-range';
        // rangeEnd is before start
        if (isBefore(rangeEnd, getCleanDate(start))) {
            if (isSame(date, rangeEnd))
                return WILL_BE_IN_RANGE_START;
            return isSame(date, getCleanDate(start)) ? WILL_BE_IN_RANGE_END : '';
        }
        // rangeEnd is after start;
        if (isSame(date, getCleanDate(start)))
            return WILL_BE_IN_RANGE_START;
        return isSame(date, rangeEnd) ? WILL_BE_IN_RANGE_END : '';
    }
    return '';
};

const createCalendar = (offsetDate, selectedDates, rangeEnd, locale, { mode, minDate, maxDate }, calendar) => {
    const { locale: localeStr, day, year: localeYear } = locale;
    const { M, Y } = getDateParts(offsetDate);
    const { start, length } = getCalendarMonthParams(M, Y, calendar);
    const days = [];
    for (let i = 1; i <= length; i++) {
        const date = newDate(Y, M, i - start);
        days.push({
            $date: date,
            day: toLocaleDateString(date, localeStr, { day }),
            now: isSame(getCleanDate(newDate()), date),
            range: getDateRangeState(date, rangeEnd, selectedDates, mode),
            disabled: minDateAndBefore(minDate, date) || maxDateAndAfter(maxDate, date),
            selected: includeDate(selectedDates, date),
            inCurrentMonth: getDateParts(date).M === M,
        });
    }
    return {
        year: toLocaleDateString(offsetDate, localeStr, { year: localeYear }),
        month: formatMonthName(offsetDate, locale),
        days,
    };
};
const createCalendars = (offsetDate, selectedDates, rangeEnd, { locale, dates, calendar }) => calendar.offsets.map((offset) => createCalendar(addToDate(offsetDate, offset, 'month'), selectedDates, rangeEnd, locale, dates, calendar));

const createWeekdays = ({ days }, { locale: { locale, weekday } }) => WEEK_DAYS.map((day) => toLocaleDateString(days[day].$date, locale, { weekday }));

const useCalendars = ({ selectedDates, state: { offsetDate, rangeEnd, config }, }) => {
    const calendars = createCalendars(offsetDate, selectedDates, rangeEnd, config);
    return {
        calendars,
        weekDays: createWeekdays(calendars[0], config),
    };
};

const SET_FOCUS_DATE_ACTION = 'SET_FOCUS_DATE';
const SET_OFFSET_DATE_ACTION = 'SET_OFFSET_DATE';
const SET_RANGE_END_ACTION = 'SET_RANGE_END';
const SET_YEAR_ACTION = 'SET_YEAR';
const stateReducer = (state, action) => {
    switch (action.type) {
        case SET_FOCUS_DATE_ACTION:
            return {
                ...state,
                focusDate: action.date,
            };
        case SET_OFFSET_DATE_ACTION:
            return {
                ...state,
                offsetDate: action.date,
            };
        case SET_RANGE_END_ACTION:
            return {
                ...state,
                rangeEnd: action.date,
            };
        case SET_YEAR_ACTION:
            return {
                ...state,
                offsetYear: action.year,
            };
        default:
            return state;
    }
};
const setFocus = (dispatch, date) => dispatch({ type: SET_FOCUS_DATE_ACTION, date });
const setOffset = (dispatch, date) => dispatch({ type: SET_OFFSET_DATE_ACTION, date });
const setRangeEnd = (dispatch, date) => dispatch({ type: SET_RANGE_END_ACTION, date });
const setYear = (dispatch, year) => dispatch({ type: SET_YEAR_ACTION, year });

const getCalendarStartDate = (minDate, maxDate, NOW) => {
    if (maxDateAndAfter(maxDate, NOW))
        return maxDate;
    if (minDateAndBefore(minDate, NOW))
        return minDate;
    return NOW;
};

/*
 * Default behavior years collection
 * It get start of the decade -1
 * It really comfortable to navigate through years
 * because you have links to the previous and next decade
 * 2019 2020 2021
 * 2022 2023 2024
 * 2025 2026 2027
 * 2028 2029 2030
 */
const getStartDecadePosition = (year) => year - (year % 10) - 1;
/*
 * If number of the year is default = 12 and current year is 2022
 * It will show this matrix
 * 2017 2018 2019
 * 2020 2021 2022 👌
 * 2023 2024 2025
 * 2026 2027 2028
 * I think it is nicer to look at the future more ;)
 */
const getFluidYearPosition = (year, numberOfYears) => year - (numberOfYears / 2 - (numberOfYears % 2 === 0 ? 1 : 0));
/*
 * It will place offsetYear at the end of the collection
 * 2015 2016 2017
 * 2012 2013 2014
 * 2018 2019 2020
 * 2021 2022 2023
 */
const getStartExactPosition = (year, numberOfYears) => year - numberOfYears + 1;
const isExactMode = (mode) => mode === 'exact';
const getCurrentYearPosition = (year, { mode, numberOfYears }) => {
    if (isExactMode(mode))
        return getStartExactPosition(year, numberOfYears);
    return mode === 'decade'
        ? getStartDecadePosition(year)
        : getFluidYearPosition(year, numberOfYears);
};

const createInitialState = (config) => {
    const { selectedDates, focusDate, dates: { minDate, maxDate }, years, } = config;
    const offsetDate = selectedDates.length > 0
        ? selectedDates[selectedDates.length - 1]
        : getCalendarStartDate(minDate, maxDate, getCleanDate(newDate()));
    return {
        focusDate,
        rangeEnd: null,
        offsetDate,
        offsetYear: getCurrentYearPosition(getDateParts(offsetDate).Y, years),
        config,
    };
};

const useDatePickerState = (config) => {
    const dpConfig = createConfig(config);
    const [state, dispatch] = useReducer(stateReducer, createInitialState(dpConfig));
    return {
        dispatch,
        selectedDates: dpConfig.selectedDates,
        state,
    };
};

const callAll = (...fns) => (...args) => fns.forEach((fn) => fn === null || fn === void 0 ? void 0 : fn(...args));
const skipFirst = (fn) => (_arg1, arg2) => fn(arg2);
const skipAll = (fn) => (..._) => {
    fn();
};

const createPropGetter = (isDisabled, action, props = {}) => ({
    role: 'button',
    tabIndex: 0,
    ...(isDisabled
        ? {
            disabled: true,
            'aria-disabled': true,
        }
        : {
            onClick(evt) {
                action(evt);
            },
        }),
    ...props,
});

const getMultipleDates = (selectedDates, date, { mode, toggle, limit }) => {
    // If toggle is active and we have already selected this date
    // Then filter it out in all modes
    if (toggle && includeDate(selectedDates, date))
        return selectedDates.filter((d) => !isSame(d, date));
    if (mode === 'multiple')
        return !limit || selectedDates.length < limit
            ? addAndSortAsc(selectedDates, date)
            : selectedDates;
    if (isRange(mode))
        return selectedDates.length === 2
            ? [date]
            : addAndSortAsc(selectedDates, date);
    // mode === 'single'
    return [date];
};

const useDays = ({ selectedDates, state: { config: { locale }, }, }) => ({
    selectedDates,
    formattedDates: selectedDates.map((d) => formatDate(d, locale)),
});
const useDaysPropGetters = ({ selectedDates, state: { config }, dispatch, }) => {
    const { onDatesChange, dates: { mode, toggle, selectSameDate }, } = config;
    const dayButton = useCallback(({ $date, selected, disabled }, { onClick, disabled: disabledProps, ...rest } = {}) => createPropGetter(disabled || !!disabledProps, (evt) => {
        if (selected && !toggle) {
            selectedDates.forEach((d) => {
                if (isSame(getCleanDate(d), $date))
                    setFocus(dispatch, d);
            });
            // Handle case when user could select same date in range mode
            if (!isRange(mode) || !selectSameDate)
                return;
        }
        if (isRange(mode) && selectedDates.length === 1) {
            setRangeEnd(dispatch, null);
        }
        callAll(onClick, skipFirst((d) => {
            if (onDatesChange && typeof onDatesChange === 'function') {
                const nextSelectedDates = getMultipleDates(selectedDates, d, config.dates);
                setFocus(dispatch, nextSelectedDates.includes(d) ? d : null);
                onDatesChange(nextSelectedDates);
            }
        }))(evt, $date);
    }, {
        ...rest,
        ...(isRange(mode) &&
            selectedDates.length === 1 && {
            onMouseEnter() {
                setRangeEnd(dispatch, $date);
            },
        }),
    }), [
        mode,
        toggle,
        config.dates,
        onDatesChange,
        selectedDates,
        dispatch,
        selectSameDate,
    ]);
    return { dayButton };
};

const createMonths = (offsetDate, selectedDates, locale, { minDate, maxDate }) => {
    const months = [];
    const { M, Y } = getDateParts(offsetDate);
    const { Y: nY, M: nM } = getDateParts(newDate());
    // Months in Date has values 0 - 11
    for (let i = 0; i < NUMBER_OF_MONTHS; i++) {
        const $date = newDate(Y, i, 1);
        months.push({
            $date,
            month: formatMonthName($date, locale),
            selected: selectedDates.some((d) => {
                const { M: dM, Y: dY } = getDateParts(d);
                return dY === Y && dM === i;
            }),
            active: M === i,
            now: i === nM && Y === nY,
            disabled: minDateAndBeforeFirstDay(minDate, $date) ||
                maxDateAndAfter(maxDate, getFirstDayOfTheMonth($date)),
        });
    }
    return months;
};

const useMonths = ({ selectedDates, state: { offsetDate, config: { locale, dates }, }, }) => ({
    months: createMonths(offsetDate, selectedDates, locale, dates),
});
const useMonthsPropGetters = ({ state: { offsetDate, config: { dates }, }, dispatch, }) => {
    const { minDate, maxDate } = dates;
    const callSetOffset = useCallback((d) => setOffset(dispatch, d), [dispatch]);
    const monthButton = useCallback(({ $date, disabled }, { onClick, disabled: disabledProps, ...rest } = {}) => createPropGetter(!!disabledProps || disabled, (evt) => callAll(onClick, skipFirst(callSetOffset))(evt, $date), rest), [callSetOffset]);
    const nextMonthButton = useCallback(({ onClick, disabled, step = 1, ...rest } = {}) => {
        const nextMonth = addToDate(offsetDate, step, 'month');
        const isDisabled = !!disabled ||
            maxDateAndAfter(maxDate, getFirstDayOfTheMonth(nextMonth));
        return createPropGetter(isDisabled, (evt) => callAll(onClick, skipFirst(callSetOffset))(evt, nextMonth), rest);
    }, [offsetDate, maxDate, callSetOffset]);
    const previousMonthButton = useCallback(({ onClick, disabled, step = 1, ...rest } = {}) => {
        const nextMonth = subtractFromDate(offsetDate, step, 'month');
        const isDisabled = !!disabled || minDateAndBeforeFirstDay(minDate, nextMonth);
        return createPropGetter(isDisabled, (evt) => callAll(onClick, skipFirst(callSetOffset))(evt, nextMonth), rest);
    }, [offsetDate, minDate, callSetOffset]);
    return { monthButton, nextMonthButton, previousMonthButton };
};
const useMonthsActions = ({ state: { offsetDate }, dispatch, }) => {
    const setMonth = useCallback((d) => setOffset(dispatch, d), [dispatch]);
    const setNextMonth = useCallback(() => setMonth(addToDate(offsetDate, 1, 'month')), [offsetDate, setMonth]);
    const setPreviousMonth = useCallback(() => setMonth(subtractFromDate(offsetDate, 1, 'month')), [offsetDate, setMonth]);
    return {
        setMonth,
        setNextMonth,
        setPreviousMonth,
    };
};

const createTime = (d, { time, locale }) => {
    const NOW = newDate();
    const { interval, minTime, maxTime } = time;
    const { Y, M, D } = getDateParts(d || NOW);
    const segments = MINUTES_IN_THE_DAY / interval;
    const t = [];
    const minDate = getTimeDate(Y, M, D, minTime);
    const maxDate = getTimeDate(Y, M, D, maxTime);
    for (let i = 0; i < segments; i++) {
        const $date = newDate(Y, M, D, 0, i * interval);
        const disabled = !d || minDateAndBefore(minDate, $date) || maxDateAndAfter(maxDate, $date);
        t.push({
            $date,
            disabled,
            now: isSame($date, NOW),
            selected: d ? isSame(d, $date) : false,
            time: formatTime($date, locale),
        });
    }
    return t;
};

const useTime = ({ state: { config, focusDate } }) => ({
    time: createTime(focusDate, config),
});
const useTimePropGetter = ({ selectedDates, state: { config, focusDate }, dispatch, }) => {
    const { onDatesChange } = config;
    const timeButton = useCallback(({ $date, selected, disabled }, { onClick, disabled: disabledProps, ...rest } = {}) => createPropGetter(disabled || !!disabledProps, (evt) => {
        if (selected)
            return;
        callAll(onClick, skipFirst((d) => {
            if (onDatesChange && typeof onDatesChange === 'function') {
                const newSelected = selectedDates.map((selected) => {
                    return isSame(focusDate, selected) ? d : selected;
                });
                setFocus(dispatch, d);
                onDatesChange(newSelected);
            }
        }))(evt, $date);
    }, rest), [selectedDates, onDatesChange, dispatch, focusDate]);
    return { timeButton };
};

const createYears = (currentYear, offsetDate, selectedDates, { numberOfYears }, { minDate, maxDate }) => {
    const { Y, M, D } = getDateParts(offsetDate);
    const years = [];
    const { Y: nY } = getDateParts(newDate());
    for (let i = 0; i < numberOfYears; i++) {
        const year = currentYear + i;
        const $date = newDate(year, M, D);
        years.push({
            $date,
            active: Y === year,
            disabled: minDateAndBeforeFirstDay(minDate, $date) ||
                maxDateAndAfter(maxDate, getFirstDayOfTheMonth($date)),
            now: year === nY,
            selected: selectedDates.some((d) => getDateParts(d).Y === year),
            year,
        });
    }
    return years;
};

const useYears = ({ selectedDates, state: { offsetDate, offsetYear, config: { years, dates }, }, }) => ({
    years: createYears(offsetYear, offsetDate, selectedDates, years, dates),
});
const useYearsPropGetters = ({ state: { offsetYear, offsetDate, config: { dates, years: yearsConfig }, }, dispatch, }) => {
    const { minDate, maxDate } = dates;
    const { step, numberOfYears, mode } = yearsConfig;
    const { D, M } = getDateParts(offsetDate);
    const callSetOffset = useCallback((d) => setOffset(dispatch, d), [dispatch]);
    const yearButton = useCallback(({ $date, disabled }, { onClick, disabled: disabledProps, ...rest } = {}) => createPropGetter(!!disabledProps || disabled, (evt) => callAll(onClick, skipFirst(callSetOffset))(evt, $date), rest), [callSetOffset]);
    const nextYearsButton = useCallback(({ onClick, disabled, ...rest } = {}) => {
        const endYearDate = newDate(offsetYear + numberOfYears - 1, M, D);
        const isDisabled = !!disabled ||
            maxDateAndAfter(maxDate, endYearDate) ||
            (isExactMode(mode) && !!maxDate && isSame(maxDate, endYearDate));
        return createPropGetter(isDisabled, (evt) => callAll(onClick, skipAll(() => setYear(dispatch, offsetYear + step)))(evt), rest);
    }, [maxDate, dispatch, offsetYear, step, D, M, numberOfYears, mode]);
    const previousYearsButton = useCallback(({ onClick, disabled, ...rest } = {}) => {
        const isDisabled = !!disabled || minDateAndBefore(minDate, newDate(offsetYear, M, D));
        return createPropGetter(isDisabled, (evt) => callAll(onClick, skipAll(() => setYear(dispatch, offsetYear - step)))(evt), rest);
    }, [minDate, dispatch, offsetYear, step, M, D]);
    return {
        yearButton,
        nextYearsButton,
        previousYearsButton,
    };
};
const useYearsActions = ({ state: { offsetYear, config: { years }, }, dispatch, }) => {
    const { step } = years;
    const setYearAction = useCallback((d) => setOffset(dispatch, d), [dispatch]);
    const setNextYears = useCallback(() => setYear(dispatch, offsetYear + step), [offsetYear, step, dispatch]);
    const setPreviousYears = useCallback(() => setYear(dispatch, offsetYear - step), [offsetYear, step, dispatch]);
    return {
        setYear: setYearAction,
        setNextYears,
        setPreviousYears,
    };
};

const useDatePicker = (config) => {
    const dpState = useDatePickerState(config);
    return {
        data: {
            ...useCalendars(dpState),
            ...useDays(dpState),
            ...useMonths(dpState),
            ...useTime(dpState),
            ...useYears(dpState),
        },
        propGetters: {
            ...useDaysPropGetters(dpState),
            ...useMonthsPropGetters(dpState),
            ...useTimePropGetter(dpState),
            ...useYearsPropGetters(dpState),
        },
        actions: {
            ...useMonthsActions(dpState),
            ...useYearsActions(dpState),
        },
    };
};

const DatePickerContext = createContext({});
const useDatePickerContext = () => useContext(DatePickerContext);
const DatePickerProvider = ({ children, config = {}, }) => (React.createElement(DatePickerContext.Provider, { value: useDatePicker(config) }, children));

const DatePickerStateContext = createContext({});
const useDatePickerStateContext = () => useContext(DatePickerStateContext);
const DatePickerStateProvider = ({ children, config = {}, }) => (React.createElement(DatePickerStateContext.Provider, { value: useDatePickerState(config) }, children));

const useContextCalendars = () => useCalendars(useDatePickerStateContext());
const useContextDays = () => useDays(useDatePickerStateContext());
const useContextDaysPropGetters = () => useDaysPropGetters(useDatePickerStateContext());
const useContextMonths = () => useMonths(useDatePickerStateContext());
const useContextMonthsPropGetters = () => useMonthsPropGetters(useDatePickerStateContext());
const useContextMonthsActions = () => useMonthsActions(useDatePickerStateContext());
const useContextYears = () => useYears(useDatePickerStateContext());
const useContextYearsPropGetters = () => useYearsPropGetters(useDatePickerStateContext());
const useContextYearsActions = () => useYearsActions(useDatePickerStateContext());
const useContextTime = () => useTime(useDatePickerStateContext());
const useContextTimePropGetters = () => useTimePropGetter(useDatePickerStateContext());

export { DatePickerProvider, DatePickerStateProvider, useCalendars, useContextCalendars, useContextDays, useContextDaysPropGetters, useContextMonths, useContextMonthsActions, useContextMonthsPropGetters, useContextTime, useContextTimePropGetters, useContextYears, useContextYearsActions, useContextYearsPropGetters, useDatePicker, useDatePickerContext, useDatePickerState, useDatePickerStateContext, useDays, useDaysPropGetters, useMonths, useMonthsActions, useMonthsPropGetters, useTime, useTimePropGetter, useYears, useYearsActions, useYearsPropGetters };
//# sourceMappingURL=index.esm.mjs.map
